/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { NavigationEnd, Router } from '@angular/router';
import { LabelsService } from '@services/labels/labels.service';
import { Injectable } from '@angular/core';
import { ModalPrefaceComponent } from '@shared/components/modal-preface/modal-preface.component';
import { ModalPrefaceStepsComponent } from '@shared/components/modal-preface-steps/modal-preface-steps.component';
import { ModalTooltipComponent } from '@shared/components/modal-tooltip/modal-tooltip.component';
import { PopoverTooltipComponent } from '@shared/components/popover-tooltip/popover-tooltip.component';
import { ModalErrorComponent } from '@core/components/modal-error/modal-error.component';
import {
  LoadingController,
  ToastController,
  ModalController,
  AlertController,
  ModalOptions,
  PopoverController
} from '@ionic/angular';
import { filter } from 'rxjs/operators';
import { slideInAnimation } from '@core/animations';
import {
  AppLoaderOptionsModel,
  BulletItemModel,
  ErrorAction,
  ErrorModalOptionsModel
} from '@core/models';
import { Loader } from './loader';
import { ModalConfirmationComponent } from '@core/modules/shared/components/modal-confirmation/modal-confirmation.component';
import { AppService } from '../app/app.service';
import { ModalLoaderComponent } from '@core/components/modal-loader/modal-loader.component';

@Injectable({
  providedIn: 'root'
})
export class UiService {
  private loader = new Loader();

  private loaderActive = false;

  private popupLoaderId: string;

  private modalLoaderId: string;

  constructor(
    private router: Router,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private modalController: ModalController,
    private alertController: AlertController,
    private popoverController: PopoverController,
    private labelsService: LabelsService,
    private appService: AppService
  ) {
    this.loader.getWhenStop().subscribe(async () => {
      await this.dismissLoader();
    });
  }

  /**
   * visalizza app loader
   */
  async showAppLoader(options: AppLoaderOptionsModel = {}): Promise<void> {
    this.loader.increase();

    // dopo che è stato creato il loader eseguo un ping in modo che
    // se nel frattempo che è stato creato qualcuno ha eseguito l'hide
    // faccio rieseguire il dismissloader, altrimenti potrebbe rimanere visibile
    // in quanto con il primo hide il loader ancora non esiste (essendo asincrona la sua creazione)
    if (!this.loaderActive) {
      await this.createLoader(options);
      this.loader.ping();
    }
  }

  /**
   * nasconde app loader
   */
  async hideAppLoader(force?: boolean): Promise<void> {
    if (force === true) {
      this.loader.reset();
    } else {
      this.loader.decrease();
    }
  }

  /**
   * visualizza toast di errore
   */
  async showErrorToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'error' });
  }

  /**
   * visualizza toast di successo
   */
  async showSuccessToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'success' });
  }

  /**
   * visualizza toast di warning
   */
  async showWarningToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'warning' });
  }

  /**
   * visualizza toast di info
   */
  async showInfoToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'info' });
  }

  /**
   * mostra toast
   */
  async showToast(
    message: string,
    options?: { type?: 'error' | 'warning' | 'success' | 'info' }
  ): Promise<void> {
    let duration = 2000;

    if (options && (options.type === 'error' || options.type === 'warning')) {
      duration = 4000;
    }

    const toast = await this.toastController.create({
      message: this.labelsService.getLabel(message),
      duration,
      position: 'top',
      color: this.getMessageColor(options && options.type)
    });

    await toast.present();
  }

  /**
   * mostra modale di prefazione
   */
  async showPreface(
    message: string | string[],
    options?: { title?: string; figure?: string }
  ): Promise<void> {
    await this.showModal({
      component: ModalPrefaceComponent,
      componentProps: {
        title: (options && options.title) || null,
        messages:
          message instanceof Array
            ? [...message]
            : typeof message === 'string'
            ? [message]
            : [],
        figure: (options && options.figure) || null
      },
      cssClass: 'modal-preface'
    });
  }

  /**
   * mostra modale di prefazione con step
   */
  async showPrefaceSteps(
    steps: {
      message: string;
      figure?: string;
    }[],
    tips?: { start: string; end: string }
  ): Promise<void> {
    await this.showModal({
      component: ModalPrefaceStepsComponent,
      componentProps: {
        steps: steps || [],
        startTip: (tips && tips.start) || null,
        endTip: (tips && tips.end) || null
      },
      cssClass: 'modal-preface'
    });
  }

  /**
   * mostra modale di errore
   */
  async showErrorModal(
    options: ErrorModalOptionsModel,
    controller?: ModalController
  ): Promise<{
    action: ErrorAction;
    id?: string;
  }> {
    return await this.showModal<{
      action: ErrorAction;
      id?: string;
    }>(
      {
        component: ModalErrorComponent,
        componentProps: {
          icon: options.icon,
          title: options.title,
          message: options.message,
          instructions: options.instructions,
          id: options.id,
          code: options.code,
          buttons: options.buttons
        },
        cssClass: 'modal-page modal-error',
        backdropDismiss: false
      },
      controller
    );
  }

  /**
   * apre modale;
   * è possibile specificare il controller adibito ad aprire la modale per
   * garantire che le depency richieste dalla modale siano proveddute
   */
  async showModal<T>(
    options: ModalOptions,
    controller?: ModalController
  ): Promise<T> {
    if (!(controller instanceof ModalController)) {
      controller = this.modalController;
    }

    const modal = await controller.create(options);

    return new Promise((resolve, reject) => {
      modal
        .onWillDismiss<T>()
        .then(res => resolve(res.data))
        .catch(reason => reject(reason));

      modal.present().then(() => this.fixOverlayEmptyElements('modal'));
    });
  }

  /**
   * apre modale di alert
   */
  async showAlert(title: string, question: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.labelsService.getLabel(title),
      message: this.labelsService.getLabel(question),
      buttons: [{ role: 'ok', text: this.labelsService.getLabel('btn.ok') }]
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    return role === 'ok';
  }

  /**
   * apre modale di conferma
   */
  async showConfirm(options: {
    title: string;
    question: string;
    confirmText?: string;
    cancelText?: string;
    interface?: 'alert' | 'modal';
  }): Promise<boolean> {
    const inerface =
      this.appService.getConfig().theme.confirmationInterface ||
      options.interface ||
      'alert';

    if (!options.confirmText) {
      options.confirmText = 'btn.confirm';
    }

    if (!options.cancelText) {
      options.cancelText = 'btn.dismiss';
    }

    if (inerface === 'modal') {
      return this.showConfirmAsModal(options);
    } else {
      return this.showConfirmAsAlert(options);
    }
  }

  /**
   * apre il tooltip come popover
   */
  async showTooltipAsPopover(options: {
    title: string;
    event?: Event;
  }): Promise<void> {
    const popover = await this.popoverController.create({
      component: PopoverTooltipComponent,
      componentProps: { tooltip: options.title },
      cssClass: 'tooltip-pover',
      translucent: true,
      event: options.event
    });

    await popover.present();
  }

  /**
   * apre il tooltip come modale
   */
  async showTooltipAsModal(options: {
    title: string;
    description: string;
    bullets?: BulletItemModel[];
  }): Promise<void> {
    const modal = await this.modalController.create({
      component: ModalTooltipComponent,
      componentProps: {
        title: options.title,
        description: options.description,
        bullets: options.bullets
      },
      enterAnimation: el => slideInAnimation(el, { direction: 'enter' }),
      leaveAnimation: el => slideInAnimation(el, { direction: 'leave' })
    });

    await modal.present();

    this.fixOverlayEmptyElements('modal');
  }

  /**
   * chiude la modale che risulta attualmente aperta
   */
  async closeOpenModal(): Promise<void> {
    const modal = await this.modalController.getTop();

    if (modal && modal.id && modal.cssClass !== 'modal-error') {
      this.modalController.dismiss(null, null, modal.id);
    }
  }

  /**
   * chiude la modale che risulta attualmente aperta
   */
  async closeOpenPopover(): Promise<void> {
    const popover = await this.popoverController.getTop();

    if (popover && popover.id) {
      this.popoverController.dismiss(null, null, popover.id);
    }
  }

  /**
   * chiude le modali e i pover rimasti eventulamente aperti ad ogni cambio di pagina;
   * assicurarsi di chiamarla una volta sola
   */
  ensureOverlayClosingOnPageChange(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeOpenModal();
        this.closeOpenPopover();
      });
  }

  /**
   * mantiene sincronizzata la variabile css per assicurare il fullscreen;
   * assicurarsi di chiamarla una volta sola
   */
  ensureFullscreen(): void {
    this.updateVhProperty();

    window.addEventListener('resize', () => {
      this.updateVhProperty();
    });
  }

  /**
   * aggiorna la var css vh per calcolare la reale altezza della viewport;
   * questo è un trick per poter definire elementi che siano alti esattamente
   * quanto la finestra ma non risentano della status bar che scompare quando si scrolla
   * (https://css-tricks.com/the-trick-to-viewport-units-on-mobile/)
   */
  updateVhProperty(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  /**
   * naviga al top della pagina
   */
  scrollToTop(): void {
    const content = this.getTopContent();
    const scrollElement =
      content && content.shadowRoot.querySelector('.inner-scroll');

    if (!!scrollElement) {
      scrollElement.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });

      this.focusOnTop();
    }
  }

  /**
   * ti fa il focus al top della pagina
   */
  focusOnTop(): void {
    const content = this.getTopContent();
    const element: HTMLElement =
      content && content.querySelector('.step-header .logo');

    if (!!element) {
      element.tabIndex = 0;
      element.focus();
    }
  }

  /**
   * trova il content che risulta on top
   */
  getTopContent(): Element {
    const modalContent = document.querySelector('ion-modal ion-content');
    const pageContent = document.querySelector(
      'ion-app .ion-page:not(.ion-page-hidden) ion-content'
    );

    return !!modalContent ? modalContent : pageContent;
  }

  /**
   * applica ai div presenti dentro modal con tabindex 0 l'attributo
   * aria-hidden=true, altimenti vengono considerati dallo screen reader
   * anche se sono vuoti e non visibili
   */
  fixOverlayEmptyElements(target: 'modal' | 'popover'): void {
    const selector = `ion-${target} [tabindex="0"]`;

    document.querySelectorAll(selector).forEach((el: HTMLElement) => {
      if (el.innerHTML.trim() === '') {
        el.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /**
   * crea il loader
   */
  private async createLoader(options: AppLoaderOptionsModel): Promise<void> {
    this.loaderActive = true;

    if (options.type === 'modal') {
      await this.createModalLoader(options);
    } else {
      await this.createPopupLoader(options);
    }

    this.loaderActive = true;
  }

  /**
   * crea loader come popup
   */
  private async createPopupLoader(
    options: AppLoaderOptionsModel
  ): Promise<void> {
    const loader = await this.loadingController.create({
      cssClass: 'app-loader',
      message: this.labelsService.getLabel(
        options.message || 'lbl.app_waiting_short'
      )
    });

    await loader.present();

    this.popupLoaderId = loader.id;
  }

  /**
   * crea loader come modale
   */
  private async createModalLoader(
    options: AppLoaderOptionsModel
  ): Promise<void> {
    const title = options.title || options.message || 'lbl.app_waiting_long';
    const message =
      !!options.title && !!options.message ? options.message : undefined;

    const modal = await this.modalController.create({
      component: ModalLoaderComponent,
      componentProps: {
        title,
        message
      },
      cssClass: 'modal-page modal-loader',
      backdropDismiss: false
    });

    await modal.present();

    this.modalLoaderId = modal.id;
  }

  /**
   * distrugge il loader se presente
   */
  private async dismissLoader(): Promise<void> {
    this.loaderActive = false;

    await Promise.all([this.dismissPopupLoader(), this.dismissModalLoader()]);

    this.loaderActive = false;
  }

  /**
   * distrugge loader popup
   */
  private async dismissPopupLoader(): Promise<void> {
    if (!this.popupLoaderId) {
      return;
    }

    // gestisco l'eventuale errore nel caso non esiste più l'overlay corrispondente
    try {
      await this.loadingController.dismiss(null, null, this.popupLoaderId);
    } catch (_) {}
  }

  /**
   * distrugge loader modale
   */
  private async dismissModalLoader(): Promise<void> {
    if (!this.modalLoaderId) {
      return;
    }

    // gestisco l'eventuale errore nel caso non esiste più l'overlay corrispondente
    try {
      await this.modalController.dismiss(null, null, this.modalLoaderId);
    } catch (_) {}
  }

  /**
   * restituisce il colore configurato per il tipo di messaggio
   */
  private getMessageColor(type: string): string {
    const colorMap = {
      error: 'danger',
      warning: 'warning',
      success: 'success'
    };

    if (!!type && !!colorMap[type]) {
      return colorMap[type];
    } else {
      return undefined;
    }
  }

  /**
   * apre modale di conferma come alert
   */
  private async showConfirmAsAlert(options: {
    title: string;
    question: string;
    confirmText?: string;
    cancelText?: string;
  }): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.labelsService.getLabel(options.title),
      message: this.labelsService.getLabel(options.question),
      buttons: [
        {
          role: 'dismiss',
          text: this.labelsService.getLabel(options.cancelText)
        },
        {
          role: 'ok',
          text: this.labelsService.getLabel(options.confirmText)
        }
      ]
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    return role === 'ok';
  }

  /**
   * apre modale di conferma come modale
   */
  private async showConfirmAsModal(options: {
    title: string;
    question: string;
    confirmText?: string;
    cancelText?: string;
  }): Promise<boolean> {
    return await this.showModal<boolean>({
      component: ModalConfirmationComponent,
      componentProps: {
        title: options.title,
        question: options.question,
        confirmText: options.confirmText,
        cancelText: options.cancelText
      }
    });
  }
}
